import { useCallback, useEffect, useMemo, useRef } from 'react'
import { useIntl } from 'react-intl'
import RedirectWithStatus from 'components/Common/RedirectWithStatus'
import clsx from 'clsx'
import styled from 'styled-components'
import { rem } from 'polished'
import { connect } from 'react-redux'
import { useAppDispatch } from 'hooks/reduxHooks'
import { useLocation, useParams } from 'react-router-dom'
import type { AppRouteMatch } from 'components/App/Routes/routeTypes'
import { urlTransform } from 'lib/string/removeSpaceUtils'
import HeadData from 'components/Common/HeadData'
import type { SearchFormRef } from 'components/Search'
import {
  getDestinationProductPage,
  getTitle,
  getRelatedTravelItems,
  decodeUrlParams,
  getSubheading,
  getMetaHeadTitle,
  getMetaDescription,
  isExperimentOn,
} from 'lib/offer/destinationProductPageUtils'
import LayoutContainer from 'components/Common/LayoutContainer'
import Pane from 'components/Common/Pane'
import PaneBody from 'components/Common/Pane/PaneBody'
import Error404PageWithOffers from 'components/Pages/Error404PageWithOffers/Error404PageWithOffers'
import HttpStatus from 'components/Common/HttpStatus'
import SimpleRelatedTravelItems from 'components/OfferPage/SimpleRelatedTravelItems'
import { fetchPlaceByName } from 'actions/PlaceActions'
import Faq from 'components/Common/Faq'
import Breadcrumbs from 'components/Pages/DestinationProductPage/Breadcrumbs'
import SearchForm from 'components/Search/SearchForm'
import { isSearchEnabled, decodeSearchParams, getSearchKey, createSearchSessionKey, getDestinationImage } from 'lib/search/searchUtils'
import DestinationPageRelatedTravelItems from './Support/DestinationPageRelatedTravelItems'
import type { DestinationNavItemType } from 'components/Common/TopDestinationsList/TopDestinationList'
import TopDestinationList from 'components/Common/TopDestinationsList/TopDestinationList'
import { useGlobalHotelsSearchContext } from 'hooks/GlobalSearch/GlobalSearchVerticals/useGlobalHotelsSearch'
import { GLOBAL_SEARCH_INITIAL_STATE } from 'contexts/GlobalSearch/GlobalSearchState'
import GlobalSearchContextProvider from 'contexts/GlobalSearch/GlobalSearchContextProvider'
import BodyText from 'LuxKit/Typography/BodyText'
import OfferList from 'components/OfferList/OfferList'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import type { TrackingOptions } from 'hooks/Offers/useOfferList'
import useOfferList from 'hooks/Offers/useOfferList'
import useOffer from 'hooks/Offers/useOffer'
import DestinationOfferListAbTestProvider from 'contexts/OfferList/DestinationOfferListAbTestProvider'
import { ANYWHERE_PLACE_ID, HOTEL_SEARCH_TYPEAHEAD_TYPES } from 'constants/search'
import { getListName } from 'analytics/enhanced-ecommerce'
import type { OfferListEventHandler } from 'components/OfferList/OfferListEventsContext'
import {
  OfferListEventsProvider,
  OfferListEvents,
} from 'components/OfferList/OfferListEventsContext'
import useOfferListTracking from 'hooks/Offers/useOfferListTracking'
import useCustomerRatingOptions from 'hooks/Search/useCustomerRatingOptions'
import OfferCountAndSort from '../HotelSearchPage/OfferCountAndSort'
import useOfferListSortOptions from 'hooks/useOfferListSortOptions'
import { mediaQueryUp } from '../../utils/breakpoint'
import type { FilterControlChangeHandler } from '../HotelSearchPage/HotelSearchPageFilterPane'
import HotelSearchPageFilterPane from '../HotelSearchPage/HotelSearchPageFilterPane'
import type { EnabledFilters } from '../../Search/type'
import { defaultEnabledFilters } from '../../Search/type'
import { pushWithRegion, replaceSearchString } from 'actions/NavigationActions'
import { getUrlOfferListFilters } from 'selectors/routerSelectors'
import { HOTEL_SEARCH_TYPES, OFFER_TYPE_BED_BANK, OFFER_TYPE_BUNDLE_AND_SAVE, OFFER_TYPE_VILLA } from 'constants/offer'
import { deleteSearchParams, parseSearchString, setManySearchParamValues } from 'lib/url/searchUrlUtils'
import SearchField from '../HotelSearchPage/SearchField'
import useGlobalSearchURLHashVertical from '../../../hooks/GlobalSearch/useGlobalSearchURLHashVertical'
import useQueryParams from '../../../hooks/useQueryParams'
import { useScreenSizeOnly, useScreenSizeUp } from 'hooks/useScreenSize'
import getOfferListKey from 'lib/offer/offerListKey'
import isBot from 'lib/web/isBot'
import switchFunc from 'lib/function/switchFunc'
import DestinationProductPageHeader from './DestinationProductPageHeader'
import { searchEvent, impressionEventWithContext, productClickEventWithContext } from 'analytics/snowplow/events'
import { trackEvent } from 'analytics/analytics'
import { addGTMEvent } from 'api/googleTagManager'
import { searchResultDealClick, searchSessionDealClick } from 'analytics/eventDefinitions'
import { mapGlobalSearchContextToSnowplowSearchEvent, mapOfferListFiltersToFilterContexts, mapOfferListFiltersToSortContext } from 'analytics/mapSnowplowSearchTracking'
import useSearchEvent from 'hooks/Search/useSearchEvent'
import FaqWrapper from '../SearchPage/FaqWrapper'
import { replace } from 'lib/routing/appRouting'
import type { SortControlChangeHandler } from 'components/Common/OfferSortControl/OfferSortControl'
import { setSearchEvent } from 'actions/SearchActions'
import useSearchSession from '../../../hooks/Search/useSearchSession'
import config from 'constants/config'
import useExperiment from 'hooks/Optimizely/useExperiment'
import { Experiments } from 'constants/optimizely'

const productTypeToSearchVertical = switchFunc<App.SearchVertical>({
  Hotels: 'hotels',
  Tours: 'tours',
}, 'hotels')

const TopDestinationListContainer = styled(Pane)`
 padding: ${rem(16)} 0 ${rem(24)};
`

const StyledLayoutContainer = styled(LayoutContainer)`
  &.filterPane {
    background-color: ${props => props.theme.palette.neutral.surface.normalBackground};
    display: grid;
    padding: ${rem(24)} 0;
    grid-template-columns: minmax(0, 1fr);
    grid-gap: ${rem(32)};

    ${mediaQueryUp.desktop} {
      grid-template: "offer-filters offer-list" auto / ${rem(278)} minmax(0, 2fr);
      padding-top: ${rem(12)};
    }
  }

  &.fullWidth {
    padding: ${rem(16)} 0;
  }
`

const StyledHotelSearchPageFilterPane = styled(HotelSearchPageFilterPane)`
  grid-area: offer-filters;
`

const StyledVerticalSpacer = styled(VerticalSpacer)`
  grid-area: offer-list;
`

const DesktopContainer = styled.div`
  ${mediaQueryUp.tablet} {
    padding: 0 ${rem(10)} ${rem(16)} ${rem(10)};
    border-bottom: 1px solid ${props => props.theme.palette.neutral.surface.lightForeground};
    margin-top: ${rem(16)};
  }
`

const StyledSearchField = styled(SearchField)`
  max-width: 100%;
  padding-top: ${rem(12)};
  padding-bottom: 0;
`

function getDefaultHotelSearchOfferTypes(): string {
  const offerTypes = [...HOTEL_SEARCH_TYPES, OFFER_TYPE_BED_BANK]
  if (config.ENABLE_VILLA_RENTALS) {
    offerTypes.push(OFFER_TYPE_VILLA)
  }

  return offerTypes.join(',')
}

type MatchProps = {
  location?: string
  holidayType?: string
  productType: string
  locationOrHolidayType?: string
  country?: string
  state?: string
  city?: string
}

interface OwnProps {
  params: MatchProps
  url: string
}

interface Props extends OwnProps {
  regionCode: string
  isSearchEnabled: boolean
  locationToPlace: { [location: string]: App.Place }
  placesLoading: { [location: string]: boolean }
  customHeadings?: App.ContentHeadings
  windowSearch: string
  filtersFromUrl: App.OfferListFilters
}

export function DestinationProductPage({
  regionCode,
  params,
  url,
  isSearchEnabled,
  locationToPlace,
  placesLoading,
  windowSearch,
  customHeadings,
  filtersFromUrl,
}: Props) {
  const intl = useIntl()
  const { searchSortByTypes } = useOfferListSortOptions()
  const {
    location,
    holidayType,
    productType,
    shouldRedirect,
  } = decodeUrlParams(params)

  const formRef = useRef<SearchFormRef>(null)

  const dispatch = useAppDispatch()
  const queryParams = useQueryParams()
  const isSeoStaticHotelsRoute = !!(params.country || params.city)
  const shouldActivateSeoStaticHotelsExperiment = !isBot && productType === 'Hotels' && isSeoStaticHotelsRoute
  const shouldResolveSeoStaticHotelsRouteToSearch = shouldActivateSeoStaticHotelsExperiment && !!location

  useExperiment(Experiments.seoStaticHotelsDynamicResolveAb, {
    canActivate: shouldActivateSeoStaticHotelsExperiment,
    overrideTrigger: true,
  })

  const onBedbankSelectDatesPrompt = useCallback(() => {
    formRef.current?.form.current.scrollIntoView()
    formRef.current?.openDatePickerMenu()
  }, [])

  const parsedSearchParams = useMemo(() => parseSearchString(windowSearch), [windowSearch])
  const decodedSearchParams = useMemo(() => decodeSearchParams(windowSearch), [windowSearch])
  const mapViewURL = useMemo(() => `/search/map?${deleteSearchParams(windowSearch, 'sortBy')}`, [windowSearch])
  const searchKey = getSearchKey(parsedSearchParams)
  const { searchId } = useSearchEvent(searchKey)
  const searchSessionKey = createSearchSessionKey({ destinationId: parsedSearchParams.destinationId, propertyId: parsedSearchParams.propertyId, landmarkId: parsedSearchParams.landmarkId })
  const { searchSessionId } = useSearchSession(searchSessionKey)
  const place = location ? locationToPlace[location] : undefined
  const placeLoading = location ? placesLoading[location] : undefined
  const placeId = place?.id
  const canonicalName = place?.canonicalName
  const searchFormEnabled = isSearchEnabled && productType !== 'Tours-&-Cruises'
  const searchVertical = productTypeToSearchVertical(productType)
  const searchItemPlaceId = decodedSearchParams?.searchItem?.value || placeId || ''

  const searchItem = useMemo<App.SearchItem>(() => (
    {
      searchType: 'destination',
      value: searchItemPlaceId,
      placeId: searchItemPlaceId,
      format: ({
        mainText: decodedSearchParams?.searchItem?.format.mainText || place?.name || '',
        secondaryText: decodedSearchParams?.searchItem?.format.secondaryText || '',
      }),
    }), [place?.name, decodedSearchParams?.searchItem, searchItemPlaceId])
  const isExperiment = isExperimentOn(url, productType)
  const seoStaticHotelsSearch = useMemo(() => {
    if (!shouldResolveSeoStaticHotelsRouteToSearch || !place) return undefined

    return setManySearchParamValues(windowSearch, [
      { paramName: 'destinationName', paramValue: place.name },
      { paramName: 'destinationId', paramValue: place.id },
      { paramName: 'adults', paramValue: 2 },
      { paramName: 'children', paramValue: 'none' },
      { paramName: 'flexibleWithDates', paramValue: 'true' },
      { paramName: 'offerTypes', paramValue: getDefaultHotelSearchOfferTypes() },
    ])
  }, [place, shouldResolveSeoStaticHotelsRouteToSearch, windowSearch])

  const { globalHotelsSearchDispatch, globalHotelsSearchState } = useGlobalHotelsSearchContext({
    ...GLOBAL_SEARCH_INITIAL_STATE,
    searchType: 'destination',
    searchItem,
    onBedbankSelectDatesPrompt,
    searchTargetLabel: place?.name,
  }, queryParams)

  const parseCanonicalName = (canonicalName: string) => {
    const splittedCanonicalName = canonicalName.split(', ')
    return (splittedCanonicalName.length === 1 ? 'country' : 'location') + '/' + splittedCanonicalName.join('/')
  }

  useEffect(() => {
    if (!place && location) {
      dispatch(fetchPlaceByName(location))
    }
  }, [dispatch, location, place])

  useEffect(() => {
    if (productType === 'Hotels' && place && !shouldResolveSeoStaticHotelsRouteToSearch) {
      // we need to append these to the search string to ensure that filters work
      dispatch(replaceSearchString({ destinationName: place.name, destinationId: place.id, adults: 2, children: 'none', flexibleWithDates: true }))
    }
  }, [place, dispatch, productType, shouldResolveSeoStaticHotelsRouteToSearch])

  const types = getDestinationProductPage(location, holidayType, productType, undefined, intl).pageTypes

  const filters = useMemo((): App.OfferListFilters => {
    if (productType === 'Hotels') {
      return {
        ...filtersFromUrl,
        offerTypes: [...HOTEL_SEARCH_TYPES, OFFER_TYPE_BUNDLE_AND_SAVE, OFFER_TYPE_BED_BANK],
        distanceEq: 0,
      }
    }

    return {
      holidayTypes: getDestinationProductPage(location, holidayType, productType, undefined, intl)?.holidayTypes,
      destinationId: location ? placeId : undefined,
      distanceEq: 0,
      useUnifiedSearchEndpoint: true,
      offerTypes: types,
    }
  }, [productType, location, filtersFromUrl, holidayType, placeId, types, intl])

  const searchContext = useMemo(() => {
    return mapGlobalSearchContextToSnowplowSearchEvent({
      ...globalHotelsSearchState,
      eventAnalytics: { searchId },
      searchVerticals: new Set([searchVertical]),
      searchItem,
    })
  }, [globalHotelsSearchState, searchId, searchVertical, searchItem])

  const customerRatingOptions = useCustomerRatingOptions()

  const filterContexts = useMemo(() => {
    if (!filtersFromUrl) return []
    return mapOfferListFiltersToFilterContexts({ offerListFilters: filtersFromUrl, CUSTOMER_RATING_GTE: customerRatingOptions.CUSTOMER_RATING_GTE })
  }, [filtersFromUrl, customerRatingOptions])

  const sortContext = useMemo(() => {
    if (!filtersFromUrl) return undefined
    return mapOfferListFiltersToSortContext({ offerListFilters: filtersFromUrl })
  }, [filtersFromUrl])

  const tracking = useOfferListTracking(
    getListName('OfferList', 'Destination Product', 'Main Results'),
    {
      snowplow: {
        enabled: false,
      },
      ga: {
        category: 'OfferList',
        label: 'Main Results',
      },
      key: getOfferListKey(filters),
    },
  )

  const offerList = useOfferList(filters)
  const destinationProductListKey = offerList.key
  const [offer] = useOffer(offerList.offerIds[0])

  const handleFilterChange = useCallback<FilterControlChangeHandler>((params) => {
    if (productType === 'Hotels') { // If hotel search send user to search page when filter selected
      dispatch(setSearchEvent(
        getSearchKey(parseSearchString(params.search)),
        { searchEvent: { type: 'filters' } },
      ))
      dispatch(pushWithRegion('/search', params.search, undefined, params.hash))
      return
    }

    dispatch(replace({
      search: params.search,
      hash: params.hash,
    }))
  }, [dispatch, productType])

  const handleSortChange = useCallback<SortControlChangeHandler>((params) => {
    if (productType === 'Hotels') { // If hotel search send user to search page when sort selected
      dispatch(setSearchEvent(
        getSearchKey(parseSearchString(params.search)),
        { searchEvent: { type: 'sort' } },
      ))
      dispatch(pushWithRegion('/search', params.search))
      return
    }

    dispatch(replace({
      search: params.search,
    }))
    window.scrollTo({ behavior: 'instant', top: 0 })
  }, [dispatch, productType])

  const isMobileScreen = useScreenSizeOnly('mobile')
  const isDesktop = useScreenSizeUp('largeDesktop')
  const enabledFilters: EnabledFilters = { ...defaultEnabledFilters, priceFilter: false }
  const { toggleSearchVertical } = useGlobalSearchURLHashVertical()
  const openSearchMenu = useCallback(() => { toggleSearchVertical(searchVertical) }, [toggleSearchVertical, searchVertical])
  const showFilterPane = isDesktop && (!!offerList.offerCount || offerList.fetching)
  const canonicalNameRedirect = productType === 'Hotels' && canonicalName && canonicalName.toLowerCase() !== location
  const hotelHolidayTypeRedirect = productType === 'Hotels' && location && (shouldRedirect || holidayType)
  const shouldStreamSearchEvents = ['Hotels', 'Tours', 'Holiday Packages'].includes(productType) && !shouldRedirect && !placeLoading && !canonicalNameRedirect && !hotelHolidayTypeRedirect && place && searchContext.destinations && searchContext.destinations.length > 0 && (filters.destinationId || filters.destinations && filters.destinations.length > 0)
  const trackingOptions = useMemo<TrackingOptions | undefined>(() => shouldStreamSearchEvents ? ({
    searchEvent: { type: 'page_landing' },
    searchContext,
    searchSessionId,
  }) : undefined, [searchContext, shouldStreamSearchEvents, searchSessionId])
  const onListEvent = useCallback<OfferListEventHandler>((dispatchAction) => {
    // we only want to fire this event on the main offer list
    // and not the extra offer list to avoid dup events
    if (dispatchAction.type === OfferListEvents.listLoad && destinationProductListKey === dispatchAction.key && shouldStreamSearchEvents) {
      trackEvent(searchEvent('offer-list-load', `destination-${searchVertical}-product-page`, {
        filters,
        searchItem: decodedSearchParams.searchItem,
        vertical: searchVertical,
      }))
    } else if (dispatchAction.type === OfferListEvents.productClick) {
      const { offer, position, availability, price, memberPrice, duration } = dispatchAction
      addGTMEvent(searchResultDealClick())
      addGTMEvent(searchSessionDealClick(searchSessionId, offer.id))
      trackEvent(
        productClickEventWithContext(
          position + 1,
          tracking.listName,
          tracking.listId,
          offer,
          { productAvailable: availability, price, memberPrice, duration, searchContext, filterContexts, sortContext },
        ),
      )
    } else if (dispatchAction.type === OfferListEvents.productImpression) {
      const { offer, position, availability, price, memberPrice, duration, roomRateId } = dispatchAction
      trackEvent(
        impressionEventWithContext(
          position + 1,
          tracking.listName,
          tracking.listId,
          offer,
          {
            productAvailable: availability,
            price: availability === false || price === undefined ? undefined : price,
            memberPrice: availability === false ? undefined : memberPrice,
            duration,
            roomRateId,
            searchContext,
            filterContexts,
            sortContext,
          },
        ),
      )
    }
  }, [destinationProductListKey, filters, decodedSearchParams.searchItem, searchVertical, shouldStreamSearchEvents, tracking.listName, tracking.listId, searchContext, filterContexts, sortContext, searchSessionId])

  if (canonicalNameRedirect) {
    return <RedirectWithStatus code={301} to={`/${regionCode.toLowerCase()}/hotels/${urlTransform(parseCanonicalName(canonicalName).toLowerCase())}`} />
  }

  if (hotelHolidayTypeRedirect) {
    return <RedirectWithStatus code={301} to={`/${regionCode.toLowerCase()}/hotels/${urlTransform(parseCanonicalName(location.toLowerCase()))}`} />
  }

  if (shouldRedirect) {
    if (productType === 'Hotels' && location) {
      return <RedirectWithStatus code={301} to={`/${regionCode.toLowerCase()}/hotels/${urlTransform(parseCanonicalName(location.toLowerCase()))}`} />
    }
    // These can be removed once we have ported all the product types to the above
    // https://github.com/lux-group/svc-public-offer-feed/blob/ad20cba1c56afb21b630d85b9ff4472f64c9ed7e/src/context/destinationProductPages.js#L10437-L10442
    if (params.locationOrHolidayType !== undefined) {
      return <RedirectWithStatus code={301} to={`/${regionCode.toLowerCase()}/${params.locationOrHolidayType.toLowerCase()}.${params.productType?.toLowerCase()}`} />
    }
    return <RedirectWithStatus code={301} to={`/${regionCode.toLowerCase()}/${params.location?.toLowerCase()}.${params.holidayType?.toLowerCase()}.${params.productType?.toLowerCase()}`} />
  }

  if (shouldResolveSeoStaticHotelsRouteToSearch) {
    if (!seoStaticHotelsSearch) return null

    return <RedirectWithStatus
      code={302}
      to={`/${regionCode.toLowerCase()}/search?${seoStaticHotelsSearch}`}
      withSearchAndHash={false}
    />
  }

  if (place === null || productType === undefined || (location === undefined && holidayType === undefined)) {
    return <HttpStatus code={404}><Error404PageWithOffers /></HttpStatus>
  }

  const headTitle = getMetaHeadTitle(regionCode, location, holidayType, productType, canonicalName, isExperiment, intl)
  const title = customHeadings?.heading ?? getTitle(location, holidayType, productType, canonicalName, intl)
  const metaDescription = getMetaDescription(location, holidayType, productType, canonicalName, isExperiment, intl)
  const relatedTravelItems = getRelatedTravelItems(location, holidayType, productType, canonicalName, intl)
  const subheading = customHeadings?.subHeading ?? getSubheading(location, productType, canonicalName, intl)
  const isCountry = !params.city && params.country
  const isCity = params.city

  if (!place) {
    filters.destinationId = ANYWHERE_PLACE_ID
  }

  let navItems: Array<DestinationNavItemType> = []
  if (isCountry) navItems = ['Countries', 'Popular', 'Cities']
  if (isCity) navItems = ['Cities', 'Popular', 'Countries']
  let heroImageId: string | undefined
  if (placeId) {
    heroImageId = getDestinationImage(placeId)
  }
  // If the place is loading, we don't want to show the hero image to avoid flickering
  heroImageId = heroImageId || (!placeLoading ? config.searchFormSection.hotelsImageId : '')

  return <GlobalSearchContextProvider
    state={globalHotelsSearchState}
    dispatch={globalHotelsSearchDispatch}
  >
    <HeadData
      title={headTitle}
      description={metaDescription}
      canonical={productType.toLowerCase() === 'hotels' && location ? `/${productType.toLowerCase()}/${urlTransform(parseCanonicalName(location))}` : undefined}
      imageId={offer && 'images' in offer && offer.images[0] ? offer.images[0].id : undefined}
    />
    <Breadcrumbs location={location} holidayType={holidayType} productType={productType} regionCode={regionCode} />
    <DestinationProductPageHeader
      title={title}
      subheading={subheading}
      imageId={heroImageId}
    />
    {searchFormEnabled && (
      <>
        <DesktopContainer>
          <SearchForm
            submitOnApply
            filters={filters}
            ref={formRef}
            showDateFieldOnMobile={false}
            typeaheadTypes={HOTEL_SEARCH_TYPEAHEAD_TYPES}
          />
        </DesktopContainer>
        {isMobileScreen && (
          <StyledSearchField
            filters={filters}
            sortOptions={searchSortByTypes}
            onEdit={openSearchMenu}
            onFilterChange={handleFilterChange}
            onSortChange={handleSortChange}
          />
        )}
      </>
    )}
    {!placeLoading && (
      <OfferListEventsProvider
        tracking={tracking}
        onListEvent={onListEvent}
      >
        <DestinationOfferListAbTestProvider sourceType="destination-product">
          <StyledLayoutContainer size={showFilterPane ? 'xl' : 'lg'} className={clsx({ filterPane: showFilterPane, fullWidth: true })}>
            <StyledVerticalSpacer gap={16}>
              <OfferList
                filters={filters}
                tileStyle="search"
                fullWidth={isMobileScreen}
                interstitials={[
                  {
                    id: 'no-offers',
                    element: <BodyText variant="large" as="h2" align="center">{intl.formatMessage({
                      id: 'GGeDSJrnZoS6YF7XgmMe',
                      defaultMessage: 'No offers available',
                      description: 'Empty state message when no offers are found on the destination product page',
                    })}
                    </BodyText>,
                    show: 'hasNoOffers',
                    position: 'start',
                  },
                  {
                    id: 'heading',
                    position: 'start',
                    element: <OfferCountAndSort
                      windowSearch={windowSearch}
                      mainListTotal={offerList.offerCount!}
                      sortOptions={searchSortByTypes}
                      filters={filters}
                      destinationName={place?.name}
                      mapViewUrl={mapViewURL}
                      enabledFilters={enabledFilters}
                      shouldShowFilters={!showFilterPane}
                      fetching={offerList.fetching}
                      searchType="destination"
                      onFilterChange={handleFilterChange}
                      onSortChange={handleSortChange}
                    />,
                    show: !offerList.fetching && productType === 'Hotels',
                  },
                ]}
                trackingOptions={trackingOptions}
              />
            </StyledVerticalSpacer>
            {showFilterPane && (
              <StyledHotelSearchPageFilterPane
                filters={filters}
                mapViewURL={mapViewURL}
                search={windowSearch}
                enabledFilters={enabledFilters}
                onFilterChange={handleFilterChange}
              />
            )}
          </StyledLayoutContainer>
        </DestinationOfferListAbTestProvider>
      </OfferListEventsProvider>
    )}
    {productType !== 'Hotels' && relatedTravelItems.length > 0 && <SimpleRelatedTravelItems items={relatedTravelItems} />}
    {productType === 'Hotels' && location && navItems.length > 0 && <TopDestinationListContainer>
      <PaneBody>
        <TopDestinationList destinationNavItems={navItems} />
      </PaneBody>
    </TopDestinationListContainer>}
    {location && <Faq
      type="destinationProduct"
      maxToShow={10}
      slug={`${urlTransform(location).toLowerCase()}`}
      tags={[productType]}
      wrapper={FaqWrapper}
    />}
    {productType === 'Hotels' && relatedTravelItems.length > 1 && <DestinationPageRelatedTravelItems items={relatedTravelItems} />}
  </GlobalSearchContextProvider>
}

const mapStateToProps = (state: App.State) => ({
  locationToPlace: state.destination.locationToPlace,
  placesLoading: state.destination.placesLoading,
  regionCode: state.geo.currentRegionCode,
  isSearchEnabled: isSearchEnabled(),
  customHeadings: state.content?.secureHeadingParams?.landing,
  windowSearch: state.router.location.search,
  filtersFromUrl: getUrlOfferListFilters(state),
})

const ConnectedDestinationProductPage = connect(mapStateToProps)(DestinationProductPage)

interface RouteProps {
  match?: AppRouteMatch<MatchProps>
}

function DestinationProductPageRoute(props: RouteProps) {
  const routeParams = useParams<keyof MatchProps>() as MatchProps
  const params = props.match?.params ?? routeParams
  const location = useLocation()

  return <ConnectedDestinationProductPage params={params} url={location.pathname} />
}

export default DestinationProductPageRoute
